<script setup lang="ts">
import { computed } from "vue";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog/index";
import type { PendingParticipantRemoval } from "@/features/discussion-threads/types";

interface Props {
  open: boolean;
  pending: PendingParticipantRemoval | null;
  disabled?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  "update:open": [value: boolean];
  confirm: [];
}>();

const modelOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const confirmDisabled = computed(() =>
  Boolean(props.disabled) || !props.pending,
);

const dialogTitle = computed(() => {
  const pending = props.pending;
  if (!pending) return "¿Quitar participante?";
  if (pending.kind === "group") return "¿Quitar grupo del hilo?";
  return pending.isCurrentUser ? "¿Quitarme del hilo?" : "¿Quitar participante del hilo?";
});

const dialogDescription = computed(() => {
  const pending = props.pending;
  if (!pending) return "Esta acción impacta las notificaciones del hilo.";

  if (pending.kind === "group") {
    return `Se quitará ${pending.label} del hilo. Sus integrantes dejarán de recibir notificaciones por pertenecer a ese grupo y no van a poder ver este hilo, a menos que los vuelvas a agregar mencionando al grupo de nuevo.`;
  }

  if (pending.isCurrentUser) {
    return "Vas a dejar de recibir notificaciones y acceso a este hilo. El grupo al que perteneces seguirá en el hilo para los demás integrantes.";
  }

  return `${pending.label} dejará de recibir notificaciones y acceso a este hilo.`;
});

const confirmLabel = computed(() => {
  const pending = props.pending;
  if (!pending) return "Confirmar";
  if (pending.kind === "group") return "Quitar grupo";
  return pending.isCurrentUser ? "Quitarme" : "Quitar participante";
});

const emitConfirm = () => {
  if (confirmDisabled.value) return;
  emit("confirm");
};
</script>

<template>
  <AlertDialog v-model:open="modelOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ dialogTitle }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ dialogDescription }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel :disabled="confirmDisabled">
          Cancelar
        </AlertDialogCancel>
        <AlertDialogAction :disabled="confirmDisabled" @click="emitConfirm">
          {{ confirmLabel }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
